import { apiRequest } from '../api/client';
import { API_ENDPOINTS, withParams } from '../config/apis';

export type RawDataQueryParams = {
  chipNumber?: string;
  timingPointKeyword?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
};

export type RawDataItem = {
  id: number;
  chipNumber: string;
  epcTime: string;
  timingPointId?: number;
  timingPointName: string;
  readerDeviceName?: string;
  machineId?: string;
};

type RawDataResponseItem = {
  id?: number | string;
  rawDataId?: number | string;
  chipNumber?: string | number;
  chip?: string | number;
  epcTime?: string;
  receivedAt?: string;
  timingPointId?: number | string;
  timingPointName?: string;
  timingPoint?: {
    id?: number | string;
    timingPointName?: string;
    name?: string;
  };
  readerDeviceName?: string;
  readerName?: string;
  machineId?: string;
};

type RawDataListResponse = {
  rawData?: RawDataResponseItem[];
  rawDatas?: RawDataResponseItem[];
  items?: RawDataResponseItem[];
  content?: RawDataResponseItem[];
  totalCount?: number;
  totalElements?: number;
  total?: number;
  count?: number;
  page?: {
    totalElements?: number;
  };
};

export type RawDataListResult = {
  rows: RawDataItem[];
  totalCount: number;
};

function toNumber(value: number | string | undefined) {
  return Number(value ?? 0);
}

function normalizeRawDataItem(item: RawDataResponseItem): RawDataItem {
  const timingPoint = item.timingPoint;

  return {
    id: toNumber(item.id ?? item.rawDataId),
    chipNumber: String(item.chipNumber ?? item.chip ?? ''),
    epcTime: item.epcTime ?? item.receivedAt ?? '',
    timingPointId: toNumber(item.timingPointId ?? timingPoint?.id) || undefined,
    timingPointName: item.timingPointName ?? timingPoint?.timingPointName ?? timingPoint?.name ?? '',
    readerDeviceName: item.readerDeviceName ?? item.readerName,
    machineId: item.machineId,
  };
}

function getResponseItems(response: RawDataListResponse | RawDataResponseItem[]) {
  if (Array.isArray(response)) {
    return response;
  }

  return response.rawDatas ?? response.rawData ?? response.items ?? response.content ?? [];
}

function getResponseTotalCount(response: RawDataListResponse | RawDataResponseItem[]) {
  if (Array.isArray(response)) {
    return response.length;
  }

  const items = getResponseItems(response);

  return response.totalCount
    ?? response.totalElements
    ?? response.total
    ?? response.count
    ?? response.page?.totalElements
    ?? items.length;
}

function hasServerPagingTotal(response: RawDataListResponse | RawDataResponseItem[]) {
  if (Array.isArray(response)) {
    return false;
  }

  return response.totalCount !== undefined
    || response.totalElements !== undefined
    || response.total !== undefined
    || response.count !== undefined
    || response.page?.totalElements !== undefined;
}

function getPagedItems(
  response: RawDataListResponse | RawDataResponseItem[],
  params: RawDataQueryParams
) {
  const items = getResponseItems(response);

  if (hasServerPagingTotal(response) || params.page === undefined || params.size === undefined) {
    return items;
  }

  const start = params.page * params.size;

  return items.slice(start, start + params.size);
}

export async function fetchRawData(eventId: number, params: RawDataQueryParams = {}) {
  const response = await apiRequest<RawDataListResponse | RawDataResponseItem[]>(
    withParams(API_ENDPOINTS.rawDataList, { eventId }),
    { query: params }
  );

  return {
    rows: getPagedItems(response, params).map(normalizeRawDataItem),
    totalCount: getResponseTotalCount(response),
  };
}
